import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  ModalProps,
  Row,
  Select
} from 'antd'
import { useEffect, useState } from 'react'

import {
  usePreScreeningServiceAvailableLegalEntitiesEndpoint,
  usePreScreeningServiceCreateRiskGroupEndpoint,
  usePreScreeningServiceGetRiskGroupEndpoint,
  usePreScreeningServiceGetRiskGroupEndpointKey,
  usePreScreeningServiceGetRiskGroupListEndpointKey,
  usePreScreeningServiceUpdateRiskGroupEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { SaveConfirm, Text } from 'src/components'
import { dayMonthYearBackSlash, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler, toDayjs } from 'src/utils'

import { UsersSearch } from './UsersSearch'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

export const AddListModal = (props: Props) => {
  const { t } = useLocales()
  const queryClient = useQueryClient()

  const [selectedUsersList, setSelectedUsersList] = useState<
    { value?: string; label?: string }[]
  >([])

  const [form] = Form.useForm()

  const { data } = usePreScreeningServiceGetRiskGroupEndpoint(
    {
      id: String(props.id)
    },
    [String(props.id)],
    {
      enabled: !!props.id
    }
  )

  const { mutate: edit, isPending: isPendingEdit } =
    usePreScreeningServiceUpdateRiskGroupEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [usePreScreeningServiceGetRiskGroupListEndpointKey]
        })
        queryClient.invalidateQueries({
          queryKey: [usePreScreeningServiceGetRiskGroupEndpointKey]
        })
        form.resetFields()
        setSelectedUsersList([])
        props.onCancel()
      },
      onError: (e: ApiError) => form.setFields(formErrorHandler(e.body.errors))
    })

  const { mutate, isPending } = usePreScreeningServiceCreateRiskGroupEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [usePreScreeningServiceGetRiskGroupListEndpointKey]
      })
      props.onCancel()
    },
    onError: (e: ApiError) => form.setFields(formErrorHandler(e.body.errors))
  })

  const { data: ongList } =
    usePreScreeningServiceAvailableLegalEntitiesEndpoint()

  const ongListOptions = ongList?.data?.map((item) => ({
    value: item?.id,
    label: item?.name
  }))

  const onFinish = async () => {
    const values = await form.validateFields()

    const payload = {
      title: values.title,
      documentDate: toDayjs(values.documentDate)?.format(yearMonthDaySlash),
      documentNumber: values.documentNumber,
      legalEntityId: values.legalEntityId,
      personIds: values.personIds
    }

    if (props?.id) {
      edit({ id: props.id, requestBody: payload })
    } else {
      mutate({ requestBody: payload })
    }
  }

  const legalEntityId = Form.useWatch('legalEntityId', form)

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        documentDate: toDayjs(data.documentDate)
      })
    }
  }, [data])

  return (
    <Modal
      open
      centered
      maskClosable={false}
      title={
        <Text size="xl" weight={700}>
          {props?.id
            ? t('pre_screening.edit_list')
            : t('pre_screening.add_new_list')}
        </Text>
      }
      footer={
        <Flex justify="center" gap={8}>
          <Button onClick={props.onCancel}>{t('common.cancel')}</Button>

          <SaveConfirm
            title={`${props?.id ? t('common.edit') : t('common.add')}`}
            onConfirm={onFinish}
            okButtonProps={{ loading: isPending || isPendingEdit }}
          >
            <Button type="primary">
              {props?.id ? t('common.edit') : t('common.add')}
            </Button>
          </SaveConfirm>
        </Flex>
      }
      {...props}
      onCancel={() => {
        form.resetFields()
        setSelectedUsersList([])
        props.onCancel()
      }}
    >
      <Form layout="vertical" requiredMark={false} form={form}>
        <Flex vertical gap={16}>
          <Form.Item
            label={t('classifiers.title')}
            name="title"
            rules={requiredRules}
          >
            <Input placeholder={t('classifiers.title')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('pre_screening.document_number')}
                name="documentNumber"
                rules={requiredRules}
              >
                <Input placeholder={t('pre_screening.document_number')} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t('pre_screening.document_date')}
                name="documentDate"
                rules={requiredRules}
              >
                <DatePicker
                  className="full-width"
                  format={dayMonthYearBackSlash}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={t('pre_screening.ong')}
            name="legalEntityId"
            rules={requiredRules}
          >
            <Select
              options={ongListOptions}
              placeholder={t('pre_screening.ong')}
            />
          </Form.Item>

          <Form.Item
            label={t('support_investigation.add_people')}
            name="personIds"
            rules={requiredRules}
          >
            <UsersSearch
              id={props?.id}
              selectedUsersList={selectedUsersList}
              setSelectedUsersList={setSelectedUsersList}
              disabled={!legalEntityId}
              placeholder={t(
                'organizational_structure.department_tab.search_placeholder'
              )}
              form={form}
              institutionId={legalEntityId}
              hasRiskGroup={true}
            />
          </Form.Item>
        </Flex>
      </Form>
    </Modal>
  )
}
